import { useState, useEffect } from "react";
import { AlertTriangle, Calendar, CheckCircle, Clock, FileText, Search, Trash2, XCircle } from "lucide-react";
import { supabase } from "./supabase";
import { KO, EN, ES, fmtDate, dispType, TYPE_EN, sendPushToAdmin, sendPushToEmployee, getHolidays, getAllocatedVac, getPolicy } from "./helpers";

const LATE_STD="08:00";

export function LeaveManager({t,isAdmin,empName,refreshKey}){
  const L=t.leave;
  const [subTab,setSubTab]=useState("attend");
  const lang=t===KO?"kr":t===ES?"es":"en";
  return(
    <>
      <div className="ph">
        <div className="pt"><Clock size={20}/> {L.title}</div>
        <div className="ps">{L.sub}</div>
      </div>
      <div className="tabs" style={{marginBottom:20}}>
        <button className={`tab ${subTab==="attend"?"active":""}`} onClick={()=>setSubTab("attend")}>{L.attendTab}</button>
        <button className={`tab ${subTab==="leave"?"active":""}`} onClick={()=>setSubTab("leave")}>{L.leaveTab}</button>
        {isAdmin&&<button className={`tab ${subTab==="stats"?"active":""}`} onClick={()=>setSubTab("stats")}>{lang==="kr"?"지각/조퇴 집계":lang==="es"?"Informe de tardanzas/salidas":"Late/Early Report"}</button>}
      </div>
      {subTab==="attend" && <AttendanceRecords key={refreshKey} t={t} isAdmin={isAdmin} empName={empName} refreshKey={refreshKey}/>}
      {subTab==="leave"  && <LeaveRecords key={refreshKey} t={t} isAdmin={isAdmin} empName={empName} refreshKey={refreshKey}/>}
      {subTab==="stats"  && isAdmin && <LateEarlyReport key={refreshKey} t={t} refreshKey={refreshKey}/>}
    </>
  );
}

function AttendanceRecords({t,isAdmin,empName,refreshKey}){
  const L=t.leave;
  const lang=t===KO?"kr":t===ES?"es":"en";
  const [records,setRecords]=useState([]);
  const [loading,setLoading]=useState(true);
  const [filterEmp,setFilterEmp]=useState("all");
  const [showSearchAtt,setShowSearchAtt]=useState(false);
  const [searchAtt,setSearchAtt]=useState("all");
  const [filterStatus,setFilterStatus]=useState("all");
  const [filterMonth,setFilterMonth]=useState(()=>new Date().toISOString().slice(0,7));
  const [employees,setEmployees]=useState([]);
  const [selected,setSelected]=useState(new Set());
  const [policy,setPolicy]=useState(null);

  useEffect(()=>{
    getPolicy().then(setPolicy);
  },[]);

  const lateStdMin=parsePolicyTime(policy?.work_start_en) ?? 8*60;
  const lateStdLabel=policy?.work_start_en||LATE_STD;

  useEffect(()=>{
    supabase.from("employees").select("name").then(({data})=>{
      if(data) setEmployees(data.map(e=>e.name));
    });
    fetchRecords();
  },[refreshKey]);

  async function delAttend(id){
    if(window.confirm(lang==="kr"?"이 출퇴근 기록을 삭제하시겠습니까?":lang==="es"?"¿Eliminar este registro de asistencia?":"Delete this attendance record?")){
      await supabase.from("leaves").delete().eq("id",id);
      setSelected(s=>{const n=new Set(s);n.delete(id);return n;});
      fetchRecords();
    }
  }
  async function delSelected(){
    if(selected.size===0) return;
    if(!window.confirm(lang==="kr"?`선택한 ${selected.size}개 기록을 삭제하시겠습니까?`:lang==="es"?`¿Eliminar ${selected.size} registros seleccionados?`:`Delete ${selected.size} selected records?`)) return;
    await Promise.all([...selected].map(id=>supabase.from("leaves").delete().eq("id",id)));
    setSelected(new Set());
    fetchRecords();
  }
  async function fetchRecords(){
    setLoading(true);
    let q=supabase.from("leaves").select("*").not("timesheet","is",null);
    if(!isAdmin&&empName) q=q.eq("emp_name",empName);
    const{data}=await q
      .order("date",{ascending:false});
    if(data) setRecords(data);
    setLoading(false);
  }

  function getStatus(rec){
    if(!rec.clock_in && !rec.clock_out) return "noRecord";
    if(!rec.clock_in) return "noRecord";
    if(!rec.clock_out) return "notClockedOut";
    // 지각 체크: 정책 출근시간(work_start_en) 대비
    const inMin=parseClockTime(rec.clock_in);
    if(inMin!=null&&inMin>lateStdMin) return "late";
    return "normal";
  }

  const filtered=records.filter(r=>{
    if(searchAtt!=="all"&&!r.emp_name?.toLowerCase().includes(searchAtt.toLowerCase())) return false;
    if(filterMonth&&!r.date.startsWith(filterMonth)) return false;
    const st=getStatus(r);
    if(filterStatus==="late"&&st!=="late") return false;
    if(filterStatus==="noRecord"&&st!=="noRecord") return false;
    if(filterStatus==="abnormal"&&!r.abnormal_situation) return false;
    return true;
  });
  const allFilteredIds=filtered.map(r=>r.id);
  const allChecked=allFilteredIds.length>0&&allFilteredIds.every(id=>selected.has(id));
  function toggleAll(){
    if(allChecked){ setSelected(s=>{const n=new Set(s);allFilteredIds.forEach(id=>n.delete(id));return n;}); }
    else { setSelected(s=>{const n=new Set(s);allFilteredIds.forEach(id=>n.add(id));return n;}); }
  }
  function toggleOne(id){
    setSelected(s=>{const n=new Set(s);n.has(id)?n.delete(id):n.add(id);return n;});
  }

  const statusBadge=(rec)=>{
    const st=getStatus(rec);
    if(!st) return null;
    const cfg={
      late:{label:L.late,bg:"rgba(220,38,38,.12)",color:"var(--re)"},
      notClockedOut:{label:L.notClockedOut,bg:"rgba(217,119,6,.12)",color:"var(--ye)"},
      normal:{label:L.normal,bg:"rgba(5,150,105,.1)",color:"var(--gr)"},
      noRecord:{label:lang==="kr"?"미기록":lang==="es"?"Sin registro":"No Record",bg:"rgba(100,116,139,.12)",color:"var(--tx3)"},
    };
    const s=cfg[st]||cfg.normal;
    return <span style={{padding:"2px 10px",borderRadius:20,fontSize:11,fontWeight:700,background:s.bg,color:s.color}}>{s.label}</span>;
  };

  // 통계
  const lateCount=records.filter(r=>getStatus(r)==="late").length;
  const noRecordCount=records.filter(r=>getStatus(r)==="noRecord").length;
  const abnCount=records.filter(r=>r.abnormal_situation).length;

  return(
    <>
      {/* 통계 카드 */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
        {[
          {label:L.totalRecords||"전체 기록",val:records.length,color:"var(--ac)"},
          {label:L.late,val:lateCount,color:"var(--re)"},
          {label:lang==="kr"?"미기록":lang==="es"?"Sin registro":"No Record",val:noRecordCount,color:"var(--tx3)"},
          {label:L.abnormalHdr||"이상",val:abnCount,color:"var(--ye)"},
        ].map((s,i)=>(
          <div key={i} className="card" style={{padding:"14px 18px",textAlign:"center"}}>
            <div style={{fontSize:22,fontWeight:800,color:s.color}}>{s.val}</div>
            <div style={{fontSize:12,color:"var(--tx3)",marginTop:4}}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* 필터 */}
      <div className="card" style={{padding:"12px 16px",marginBottom:16}}>
        <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
          {isAdmin&&<>
            <button onClick={()=>{setShowSearchAtt(s=>!s);if(showSearchAtt)setSearchAtt("all");}} style={{background:"var(--sf2)",border:"1px solid var(--bd)",borderRadius:8,padding:"6px 10px",cursor:"pointer",fontSize:16,color:"var(--tx2)",lineHeight:1}}><Search size={15}/></button>
            {showSearchAtt&&<input autoFocus value={searchAtt==="all"?"":searchAtt} onChange={e=>setSearchAtt(e.target.value||"all")}
              placeholder={L.filterAll}
              style={{padding:"6px 14px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",fontSize:13,width:140}}
            />}
          </>}
          <select value={filterMonth.slice(0,4)} onChange={e=>setFilterMonth(`${e.target.value}-${filterMonth.slice(5,7)||"01"}`)}
            style={{background:"var(--sf2)",border:"1px solid var(--bd)",borderRadius:8,padding:"6px 10px",color:"var(--tx)",fontSize:13}}>
            {Array.from({length:5},(_,i)=>new Date().getFullYear()-2+i).map(y=>(
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <select value={filterMonth.slice(5,7)} onChange={e=>setFilterMonth(`${filterMonth.slice(0,4)}-${e.target.value}`)}
            style={{background:"var(--sf2)",border:"1px solid var(--bd)",borderRadius:8,padding:"6px 10px",color:"var(--tx)",fontSize:13}}>
            {(lang==="kr"?KO.months:lang==="es"?ES.months:EN.months).map((m,i)=>(
              <option key={i} value={String(i+1).padStart(2,"0")}>{m}</option>
            ))}
          </select>
          <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={{background:"var(--sf2)",border:"1px solid var(--bd)",borderRadius:8,padding:"6px 10px",color:"var(--tx)",fontSize:13}}>
            <option value="all">{L.filterAll}</option>
            <option value="late">{L.filterLate}</option>
            <option value="noRecord">{lang==="kr"?"미기록":lang==="es"?"Sin registro":"No Record"}</option>
            <option value="abnormal">{L.filterAbnormal}</option>
          </select>
          <span style={{fontSize:12,color:"var(--tx3)"}}><AlertTriangle size={13} style={{marginRight:4}}/>{L.lateStd}: {lateStdLabel}</span>
        </div>
      </div>

      {/* 선택 삭제 바 */}
      {isAdmin&&selected.size>0&&(
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,padding:"8px 14px",background:"rgba(74,122,181,.08)",borderRadius:8,border:"1px solid rgba(74,122,181,.2)"}}>
          <span style={{fontSize:13,color:"var(--ac)",fontWeight:600}}>{selected.size}{lang==="kr"?"개 선택됨":lang==="es"?" seleccionados":" selected"}</span>
          <button onClick={delSelected} style={{display:"flex",alignItems:"center",gap:4,padding:"5px 14px",borderRadius:6,border:"none",background:"var(--re)",color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>
            <Trash2 size={13}/> {lang==="kr"?"선택 삭제":lang==="es"?"Eliminar seleccionados":"Delete Selected"}
          </button>
          <button onClick={()=>setSelected(new Set())} style={{padding:"5px 12px",borderRadius:6,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx2)",fontSize:12,cursor:"pointer"}}>
            {lang==="kr"?"선택 해제":lang==="es"?"Deseleccionar todo":"Deselect All"}
          </button>
        </div>
      )}
      {/* 테이블 */}
      {loading?<div className="card"><div className="empty">{L.loading}</div></div>:
      filtered.length===0?<div className="card"><div className="empty">{L.noData}</div></div>:(
        <div className="card" style={{padding:0,overflow:"hidden"}}>
          <div style={{overflowX:"auto"}}>
            <table style={{fontSize:12,width:"100%"}}>
              <thead>
                <tr style={{background:"var(--sf2)"}}>
                  {isAdmin&&<th style={{padding:"8px 10px",borderBottom:"2px solid var(--bd)",width:36}}>
                    <input type="checkbox" checked={allChecked} onChange={toggleAll}
                      style={{cursor:"pointer",width:14,height:14,accentColor:"var(--ac)"}}/>
                  </th>}
                  {[L.colEmp,L.colDate,L.colTimesheet,L.colIn,L.colOut,
                    L.colClock,L.colBreak,L.colWork,L.colOT,L.statusHdr||"Status",L.colAbnormal,""
                  ].map(h=>(
                    <th key={h} style={{padding:"8px 10px",fontWeight:700,whiteSpace:"nowrap",
                      borderBottom:"2px solid var(--bd)",textAlign:"left",color:"var(--tx)"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((rec,i)=>(
                  <tr key={rec.id} style={{background:selected.has(rec.id)?"rgba(74,122,181,.07)":i%2===0?"":"var(--sf2)",borderBottom:"1px solid var(--bd)"}}>
                    {isAdmin&&<td style={{padding:"7px 10px",width:36}}>
                      <input type="checkbox" checked={selected.has(rec.id)} onChange={()=>toggleOne(rec.id)}
                        style={{cursor:"pointer",width:14,height:14,accentColor:"var(--ac)"}}/>
                    </td>}
                    <td style={{padding:"7px 10px",fontWeight:600,whiteSpace:"nowrap"}}>{rec.emp_name}</td>
                    <td style={{padding:"7px 10px",whiteSpace:"nowrap"}}>{rec.date}</td>
                    <td style={{padding:"7px 10px",whiteSpace:"nowrap",color:"var(--tx3)",fontSize:11}}>{rec.timesheet||"—"}</td>
                    <td style={{padding:"7px 10px",whiteSpace:"nowrap",color:"var(--ac)",fontWeight:600}}>{rec.clock_in||"—"}</td>
                    <td style={{padding:"7px 10px",whiteSpace:"nowrap",color:rec.clock_out?"var(--tx)":"var(--ye)"}}>{rec.clock_out||"—"}</td>
                    <td style={{padding:"7px 10px",whiteSpace:"nowrap"}}>{rec.clock_time||"—"}</td>
                    <td style={{padding:"7px 10px",whiteSpace:"nowrap"}}>{rec.break_time||"—"}</td>
                    <td style={{padding:"7px 10px",whiteSpace:"nowrap",fontWeight:600,color:"var(--gr)"}}>{rec.work_time||"—"}</td>
                    <td style={{padding:"7px 10px",whiteSpace:"nowrap",color:rec.ot_time&&rec.ot_time!=="00:00:00"?"var(--or)":"var(--tx3)"}}>{rec.ot_time||"—"}</td>
                    <td style={{padding:"7px 10px",whiteSpace:"nowrap"}}>{statusBadge(rec)}</td>
                    <td style={{padding:"7px 10px",color:"var(--re)",fontSize:11}}>{rec.abnormal_situation||""}</td>
                     <td style={{padding:"7px 10px"}}>{isAdmin&&<button onClick={()=>delAttend(rec.id)} style={{background:"none",border:"none",color:"var(--re)",cursor:"pointer",fontSize:13,padding:"2px 6px",borderRadius:4}} title={lang==="kr"?"삭제":lang==="es"?"Eliminar":"Delete"}><Trash2 size={13}/></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function parsePolicyTime(s){
  if(!s) return null;
  const ampm=s.match(/(\d{1,2}):(\d{2})\s*([AaPp][Mm])/);
  if(ampm){
    let h=parseInt(ampm[1])%12;
    if(/pm/i.test(ampm[3])) h+=12;
    return h*60+parseInt(ampm[2]);
  }
  const h24=s.match(/(\d{1,2}):(\d{2})/);
  if(h24) return parseInt(h24[1])*60+parseInt(h24[2]);
  return null;
}
function parseClockTime(s){
  if(!s) return null;
  const m=s.match(/(\d{1,2}):(\d{2})/);
  if(!m) return null;
  return parseInt(m[1])*60+parseInt(m[2]);
}

function LateEarlyReport({t,refreshKey}){
  const lang=t===KO?"kr":t===ES?"es":"en";
  const [records,setRecords]=useState([]);
  const [leaveRecords,setLeaveRecords]=useState([]);
  const [employees,setEmployees]=useState([]);
  const [policy,setPolicy]=useState(null);
  const [loading,setLoading]=useState(true);
  const [filterMonth,setFilterMonth]=useState(()=>new Date().toISOString().slice(0,7));
  const [detail,setDetail]=useState(null); // {name, type:"late"|"early"|"absent", items:[...]}

  useEffect(()=>{
    getPolicy().then(setPolicy);
  },[]);

  useEffect(()=>{
    fetchAll();
  },[refreshKey]);

  async function fetchAll(){
    setLoading(true);
    const[{data:att},{data:lv},{data:emp}]=await Promise.all([
      supabase.from("leaves").select("*").not("timesheet","is",null),
      supabase.from("leaves").select("*").is("timesheet",null).eq("status","approved"),
      supabase.from("employees").select("name,join_date,status"),
    ]);
    if(att) setRecords(att);
    if(lv) setLeaveRecords(lv);
    if(emp) setEmployees(emp.filter(e=>e.status!=="Inactive"));
    setLoading(false);
  }

  // 정책 시간 기준 (출근/퇴근 시간이 바뀌면 자동으로 같이 바뀜)
  const lateStdMin=parsePolicyTime(policy?.work_start_en) ?? 8*60;
  const earlyStdMin=parsePolicyTime(policy?.work_end_en) ?? 17*60;
  const lateStdLabel=policy?.work_start_en||"08:00";
  const earlyStdLabel=policy?.work_end_en||"17:00";

  const monthRecords=records.filter(r=>r.date?.startsWith(filterMonth));

  // 이번 달 평일 목록 (주말/공휴일 제외, 오늘 이후는 제외)
  const[year,monthNum]=filterMonth.split("-").map(Number);
  const holidays=getHolidays(year);
  const daysInMonth=new Date(year,monthNum,0).getDate();
  const today=new Date();
  const workdays=[];
  for(let d=1; d<=daysInMonth; d++){
    const dt=new Date(year,monthNum-1,d);
    const ds=fmtDate(dt);
    const dow=dt.getDay();
    if(dow!==0&&dow!==6&&!holidays[ds]&&dt<=today) workdays.push(ds);
  }

  // 직원별 출근기록 날짜 Set (clock_in이 실제로 찍힌 날만 "출근"으로 인정)
  const attendByEmp={};
  monthRecords.forEach(r=>{
    if(!r.emp_name||!r.clock_in) return;
    if(!attendByEmp[r.emp_name]) attendByEmp[r.emp_name]=new Set();
    attendByEmp[r.emp_name].add(r.date);
  });

  // 직원별 승인된 휴가/병가 등으로 커버되는 날짜 Set (결근 제외용)
  const leaveByEmp={};
  leaveRecords.forEach(l=>{
    if(!l.emp_name||!l.date) return;
    const start=l.date, end=l.end_date||l.date;
    if(end<`${filterMonth}-01`||start>`${filterMonth}-31`) return;
    if(!leaveByEmp[l.emp_name]) leaveByEmp[l.emp_name]=new Set();
    let cur=new Date(start+"T00:00:00"); const endD=new Date(end+"T00:00:00");
    while(cur<=endD){ leaveByEmp[l.emp_name].add(fmtDate(cur)); cur.setDate(cur.getDate()+1); }
  });

  const summary={};
  function ensure(name){ if(!summary[name]) summary[name]={late:[],early:[],absent:[]}; return summary[name]; }

  monthRecords.forEach(r=>{
    const name=r.emp_name||(lang==="kr"?"(미지정)":lang==="es"?"(Desconocido)":"(Unknown)");
    const s=ensure(name);
    const inMin=parseClockTime(r.clock_in);
    const outMin=parseClockTime(r.clock_out);
    if(inMin!=null&&inMin>lateStdMin) s.late.push({date:r.date,time:r.clock_in.slice(0,5)});
    if(outMin!=null&&outMin<earlyStdMin) s.early.push({date:r.date,time:r.clock_out.slice(0,5)});
  });

  employees.forEach(emp=>{
    const s=ensure(emp.name);
    workdays.forEach(ds=>{
      if(emp.join_date&&ds<emp.join_date) return;
      const hasAttend=attendByEmp[emp.name]?.has(ds);
      const hasLeave=leaveByEmp[emp.name]?.has(ds);
      if(!hasAttend&&!hasLeave) s.absent.push({date:ds});
    });
  });

  const rows=Object.entries(summary)
    .map(([name,v])=>({name,late:v.late,early:v.early,absent:v.absent,total:v.late.length+v.early.length+v.absent.length}))
    .filter(r=>r.total>0)
    .sort((a,b)=>b.total-a.total);

  return(
    <>
      <div className="card" style={{padding:"12px 16px",marginBottom:16}}>
        <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
          <select value={filterMonth.slice(0,4)} onChange={e=>setFilterMonth(`${e.target.value}-${filterMonth.slice(5,7)||"01"}`)}
            style={{background:"var(--sf2)",border:"1px solid var(--bd)",borderRadius:8,padding:"6px 10px",color:"var(--tx)",fontSize:13}}>
            {Array.from({length:5},(_,i)=>new Date().getFullYear()-2+i).map(y=>(
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <select value={filterMonth.slice(5,7)} onChange={e=>setFilterMonth(`${filterMonth.slice(0,4)}-${e.target.value}`)}
            style={{background:"var(--sf2)",border:"1px solid var(--bd)",borderRadius:8,padding:"6px 10px",color:"var(--tx)",fontSize:13}}>
            {(lang==="kr"?KO.months:lang==="es"?ES.months:EN.months).map((m,i)=>(
              <option key={i} value={String(i+1).padStart(2,"0")}>{m}</option>
            ))}
          </select>
          <span style={{fontSize:12,color:"var(--tx3)"}}>
            <AlertTriangle size={13} style={{marginRight:4}}/>
            {lang==="kr"?`지각 기준 ${lateStdLabel} / 조퇴 기준 ${earlyStdLabel}`:lang==="es"?`Tarde después de ${lateStdLabel} / Salida antes de ${earlyStdLabel}`:`Late after ${lateStdLabel} / Early before ${earlyStdLabel}`}
          </span>
        </div>
      </div>

      {loading?<div className="card"><div className="empty">{lang==="kr"?"불러오는 중...":lang==="es"?"Cargando...":"Loading..."}</div></div>:
      rows.length===0?<div className="card"><div className="empty">{lang==="kr"?"이번 달 지각/조퇴/결근 기록이 없습니다.":lang==="es"?"No hay registros de tardanzas, salidas anticipadas o ausencias este mes.":"No late/early/absence records this month."}</div></div>:(
        <div className="card" style={{padding:0,overflow:"hidden"}}>
          <div style={{overflowX:"auto"}}>
            <table style={{fontSize:13,width:"100%"}}>
              <thead>
                <tr style={{background:"var(--sf2)"}}>
                  {[lang==="kr"?"직원":lang==="es"?"Empleado":"Employee",lang==="kr"?"지각":lang==="es"?"Tarde":"Late",lang==="kr"?"조퇴":lang==="es"?"Salida anticipada":"Early Leave",lang==="kr"?"결근":lang==="es"?"Ausente":"Absent",lang==="kr"?"합계":lang==="es"?"Total":"Total"].map(h=>(
                    <th key={h} style={{padding:"10px 14px",fontWeight:700,whiteSpace:"nowrap",borderBottom:"2px solid var(--bd)",textAlign:"left",color:"var(--tx)"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r,i)=>(
                  <tr key={r.name} style={{background:i%2===0?"":"var(--sf2)",borderBottom:"1px solid var(--bd)"}}>
                    <td style={{padding:"9px 14px",fontWeight:600}}>{r.name}</td>
                    <td style={{padding:"9px 14px"}}>
                      {r.late.length>0
                        ?<button onClick={()=>setDetail({name:r.name,type:"late",items:r.late})} style={{background:"none",border:"none",cursor:"pointer",color:"var(--re)",fontWeight:700,fontSize:13,textDecoration:"underline",padding:0}}>{r.late.length}</button>
                        :<span style={{color:"var(--tx3)",fontWeight:700}}>0</span>
                      }
                    </td>
                    <td style={{padding:"9px 14px"}}>
                      {r.early.length>0
                        ?<button onClick={()=>setDetail({name:r.name,type:"early",items:r.early})} style={{background:"none",border:"none",cursor:"pointer",color:"var(--ye)",fontWeight:700,fontSize:13,textDecoration:"underline",padding:0}}>{r.early.length}</button>
                        :<span style={{color:"var(--tx3)",fontWeight:700}}>0</span>
                      }
                    </td>
                    <td style={{padding:"9px 14px"}}>
                      {r.absent.length>0
                        ?<button onClick={()=>setDetail({name:r.name,type:"absent",items:r.absent})} style={{background:"none",border:"none",cursor:"pointer",color:"var(--tx2)",fontWeight:700,fontSize:13,textDecoration:"underline",padding:0}}>{r.absent.length}</button>
                        :<span style={{color:"var(--tx3)",fontWeight:700}}>0</span>
                      }
                    </td>
                    <td style={{padding:"9px 14px",fontWeight:800}}>{r.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detail&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setDetail(null)}>
          <div style={{background:"var(--sf)",borderRadius:16,padding:24,width:"100%",maxWidth:380,maxHeight:"70vh",overflowY:"auto",boxShadow:"0 8px 40px rgba(0,0,0,.3)"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{fontSize:15,fontWeight:700}}>
                {detail.name} · {detail.type==="late"?(lang==="kr"?"지각":lang==="es"?"Tarde":"Late"):detail.type==="early"?(lang==="kr"?"조퇴":lang==="es"?"Salida anticipada":"Early Leave"):(lang==="kr"?"결근":lang==="es"?"Ausente":"Absent")} ({detail.items.length})
              </div>
              <button onClick={()=>setDetail(null)} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:"var(--tx3)"}}>✕</button>
            </div>
            {detail.items.sort((a,b)=>a.date.localeCompare(b.date)).map((d,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid var(--bd)",fontSize:13}}>
                <span>{d.date}</span>
                {d.time&&<span style={{fontFamily:"'DM Mono',monospace",fontWeight:700,color:detail.type==="late"?"var(--re)":"var(--ye)"}}>{d.time}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function LeaveRecords({t,isAdmin,empName,refreshKey}){
  const L=t.leave;
  const lt=L.types||["연차","병가","특이사항"];
  const lang=t===KO?"kr":t===ES?"es":"en";
  const [records,setRecords]=useState([]);
  const [employees,setEmployees]=useState([]);
  const [tab,setTab]=useState("list");
  const [editId,setEditId]=useState(null);
  const [editingOrig,setEditingOrig]=useState(null);
  const [filterEmp,setFilterEmp]=useState("all");
  const [showSearchLeave,setShowSearchLeave]=useState(false);
  const [searchLeave,setSearchLeave]=useState("all");
  const [filterType,setFilterType]=useState("all");
  const [conflictList,setConflictList]=useState([]);
  const [policy,setPolicy]=useState(null);
  useEffect(()=>{ getPolicy().then(setPolicy); },[]);
  const today=fmtDate(new Date());

  function calcWorkDays(startStr, endStr){
    if(!startStr||!endStr||startStr>endStr) return 0;
    const holidays=getHolidays(parseInt(startStr.slice(0,4)));
    const holidays2=startStr.slice(0,4)!==endStr.slice(0,4)?getHolidays(parseInt(endStr.slice(0,4))):{};
    const allHolidays={...holidays,...holidays2};
    let count=0;
    const cur=new Date(startStr+"T00:00:00");
    const end=new Date(endStr+"T00:00:00");
    while(cur<=end){
      const dow=cur.getDay();
      const ds=fmtDate(cur);
      if(dow!==0&&dow!==6&&!allHolidays[ds]) count++;
      cur.setDate(cur.getDate()+1);
    }
    return count;
  }

  const isRangetype=(type)=>type==="연차"||type==="병가"||type==="Annual Leave"||type==="Sick Leave";
  const def={name:isAdmin?"":empName||"",type:lt[0],date:today,end_date:today,days:"1",reason:"",note:""};
  const [form,setForm]=useState(def);

  useEffect(()=>{
    supabase.from("employees").select("name,join_date,position,status").order("name").then(({data})=>{
      if(data) setEmployees(data.filter(e=>e.status!=="Inactive"));
    });
    fetchRecords();
  },[refreshKey]);

  // 직원 로그인시 empName 자동 세팅
  useEffect(()=>{
    if(!isAdmin&&empName) setForm(p=>({...p,name:empName}));
  },[empName,isAdmin]);

  async function fetchRecords(){
    let q=supabase.from("leaves").select("*").is("timesheet",null);
    if(!isAdmin&&empName) q=q.eq("emp_name",empName);
    const{data}=await q.order("date",{ascending:false});
    if(data) setRecords(data);
  }

  function sf(k,v){setForm(p=>({...p,[k]:v}));}
  async function checkConflicts(startDate,endDate){
    const{data}=await supabase.from("leaves").select("emp_name,date,end_date,type").is("clock_in",null).eq("status","approved").in("type",["연차","병가","Annual Leave","Sick Leave"]);
    const conflicts=(data||[]).filter(l=>{
      const lEnd=l.end_date||l.date;
      return !(endDate<l.date||startDate>lEnd);
    });
    setConflictList(conflicts);
  }
  function startEdit(r){setForm({name:r.emp_name||r.name||"",type:r.type,date:r.date,end_date:r.end_date||r.date,days:r.days||"1",reason:r.reason||"",note:r.note||""});setEditId(r.id);setEditingOrig(r);setTab("add");}

  async function save(){
    if((!form.name&&isAdmin)||!form.date) return alert(L.empSel);
    const useRange=isRangetype(form.type);
    const endDate=useRange?(form.end_date||form.date):form.date;
    if(useRange&&endDate<form.date){ alert(lang==="kr"?"종료일이 시작일보다 빠릅니다.":lang==="es"?"La fecha final debe ser posterior a la fecha de inicio.":"End date must be after start date."); return; }
    const workDays=useRange?calcWorkDays(form.date,endDate):1;
    // 중복 체크
    if(!editId){
      const{data:existing}=await supabase.from("leaves").select("id,date,end_date,type").eq("emp_name",form.name).is("clock_in",null).in("type",["연차","병가","Annual Leave","Sick Leave"]);
      const hasDup=(existing||[]).some(r=>{
        const rEnd=r.end_date||r.date;
        return !(endDate<r.date||form.date>rEnd);
      });
      if(hasDup){
        alert(lang==="kr"?"해당 기간에 이미 등록된 기록이 있습니다. 중복 신청을 확인해주세요.":lang==="es"?"Ya existe un registro para este período. Verifique que no esté duplicado.":"A record already exists for this period. Please check for duplicates.");
        return;
      }
    }
    // 잔여일수 경고 체크 (연차/병가)
    const thisYear=form.date.slice(0,4);
    if((form.type==="연차"||form.type==="Annual Leave")&&!editId){
      const emp=employees.find(e=>e.name===form.name);
      const allocated=emp?getAllocatedVac(emp,policy?.vacation_tiers):0;
      const{data:usedData}=await supabase.from("leaves").select("days").eq("emp_name",form.name).in("type",["연차","Annual Leave"]).eq("status","approved").gte("date",`${thisYear}-01-01`).lte("date",`${thisYear}-12-31`);
      const usedDays=(usedData||[]).reduce((s,l)=>s+parseFloat(l.days||0),0);
      const remaining=allocated-usedDays;
      if(workDays>remaining){
        const msg=isAdmin
          ?(lang==="kr"?`${form.name}의 잔여 연차가 ${remaining}일인데 ${workDays}일을 입력했습니다. 그래도 진행하시겠습니까?`:lang==="es"?`A ${form.name} le quedan ${remaining} días de vacaciones pero ingresó ${workDays} días. ¿Desea continuar?`:`${form.name} has ${remaining} vacation days remaining but you entered ${workDays} days. Proceed anyway?`)
          :(lang==="kr"?`잔여 연차가 ${remaining}일인데 ${workDays}일을 신청했습니다. 그래도 신청하시겠습니까?`:lang==="es"?`Le quedan ${remaining} días de vacaciones pero solicitó ${workDays} días. ¿Desea continuar?`:`You have ${remaining} vacation days remaining but requested ${workDays} days. Proceed anyway?`);
        const ok=window.confirm(msg);
        if(!ok) return;
      }
    }
    if((form.type==="병가"||form.type==="Sick Leave")&&!editId){
      const{data:usedData}=await supabase.from("leaves").select("days").eq("emp_name",form.name).in("type",["병가","Sick Leave"]).eq("status","approved").gte("date",`${thisYear}-01-01`).lte("date",`${thisYear}-12-31`);
      const usedDays=(usedData||[]).reduce((s,l)=>s+parseFloat(l.days||0),0);
      const remaining=5-usedDays;
      if(workDays>remaining){
        const msg=isAdmin
          ?(lang==="kr"?`${form.name}의 잔여 병가가 ${remaining}일인데 ${workDays}일을 입력했습니다. 그래도 진행하시겠습니까?`:lang==="es"?`A ${form.name} le quedan ${remaining} días de baja por enfermedad pero ingresó ${workDays} días. ¿Desea continuar?`:`${form.name} has ${remaining} sick days remaining but you entered ${workDays} days. Proceed anyway?`)
          :(lang==="kr"?`잔여 병가가 ${remaining}일인데 ${workDays}일을 신청했습니다. 그래도 신청하시겠습니까?`:lang==="es"?`Le quedan ${remaining} días de baja por enfermedad pero solicitó ${workDays} días. ¿Desea continuar?`:`You have ${remaining} sick days remaining but requested ${workDays} days. Proceed anyway?`);
        const ok=window.confirm(msg);
        if(!ok) return;
      }
    }
    const payload={emp_name:form.name,type:form.type,date:form.date,end_date:useRange?endDate:null,days:String(useRange?workDays:1),reason:form.reason,note:form.note,status:editId?(editingOrig?.status||"pending"):((isAdmin||form.type==="병가")?"approved":"pending")};
    if(editId){
      await supabase.from("leaves").update(payload).eq("id",editId);
      if(!isAdmin){
        // 직원이 승인된 자기 휴가를 직접 수정한 경우 → 관리자에게 알림 (새 신청 알림과는 별개)
        const dateMsg=useRange&&endDate!==form.date?`${form.date} ~ ${endDate}`:`${form.date}`;
        await sendPushToAdmin(supabase, "휴가 수정", `${form.name}님이 ${dateMsg} ${form.type} 내용을 수정했습니다.`, "Leave Updated", `${form.name} updated their ${TYPE_EN[form.type]||form.type} on ${dateMsg}.`, "/leave", {title:"Permiso actualizado", body:`${form.name} actualizó su ${dispType(form.type,"es")} del ${dateMsg}.`});
      }
    }
    else{
      await supabase.from("leaves").insert([payload]);
      const dateMsg=useRange&&endDate!==form.date?`${form.date} ~ ${endDate}`:`${form.date}`;
      if(!isAdmin){
        // 직원이 신청 → 관리자에게 알림
        await supabase.from("notifications").insert([{
          type:"leave_request",
          emp_name:form.name,
          message:lang==="kr"?`${form.name}님이 ${dateMsg} ${form.type} 신청했습니다.`:`${form.name} requested ${form.type} on ${dateMsg}.`,
          read:false
        }]);
        await sendPushToAdmin(supabase, "휴가 신청", `${form.name}님이 ${dateMsg} ${form.type === "연차" ? "연차를" : form.type === "병가" ? "병가를" : form.type + "을"} 신청했습니다.`, "Leave Request", `${form.name} requested ${TYPE_EN[form.type]||form.type} on ${dateMsg}.`, "/leave", {title:"Solicitud de permiso", body:`${form.name} solicitó ${dispType(form.type,"es")} para el ${dateMsg}.`});
        // 병가는 자동승인이라 직원에게도 승인 알림 발송
        if(form.type==="병가"||form.type==="Sick Leave"){
          await sendPushToEmployee(supabase, form.name, "병가 신청 완료", `${dateMsg} 병가 신청이 완료되었습니다.`, "Sick Leave Approved", `Your sick leave request for ${dateMsg} has been approved.`, "/leave", {title:"Baja por enfermedad aprobada", body:`Su solicitud de baja por enfermedad del ${dateMsg} fue aprobada.`});
        }
      } else {
        // 관리자가 등록 → 해당 직원에게 알림
        if(form.type==="병가"||form.type==="Sick Leave"){
          await sendPushToEmployee(supabase, form.name, "병가 등록", `${dateMsg} 병가가 관리자에 의해 등록되었습니다.`, "Sick Leave Registered", `Your sick leave for ${dateMsg} has been registered by the admin.`, "/leave", {title:"Baja por enfermedad registrada", body:`El administrador registró su baja por enfermedad del ${dateMsg}.`});
        }
        if(form.type==="특이사항"||form.type==="Note"||form.type==="Special Note"){
          await sendPushToEmployee(supabase, form.name, "특이사항 등록", `${dateMsg} 특이사항이 관리자에 의해 등록되었습니다.`, "Note Registered", `A note for ${dateMsg} has been registered by the admin.`, "/leave", {title:"Nota registrada", body:`El administrador registró una nota para el ${dateMsg}.`});
        }
      }
    }
    setForm({...def,name:isAdmin?"":empName||""});setEditId(null);setEditingOrig(null);fetchRecords();setTab("list");
  }

  async function approveLeave(id){
    const rec=records.find(r=>r.id===id);
    await supabase.from("leaves").update({status:"approved"}).eq("id",id);
    const typeLabel=lang==="kr"?(rec?.type==="연차"?"연차":rec?.type==="병가"?"병가":"특이사항"):(TYPE_EN[rec?.type]||rec?.type);
    const titleKr=rec?.type==="특이사항"?"특이사항 승인":"휴가 승인";
    const titleEn=rec?.type==="특이사항"|rec?.type==="Note"?"Note Approved":"Leave Approved";
    await supabase.from("notifications").insert([{
      type:"leave_approved",
      emp_name:rec?.emp_name||"",
      message:lang==="kr"?`${rec?.emp_name}님의 ${typeLabel} 신청이 승인되었습니다.`:`${rec?.emp_name}'s ${typeLabel} request approved.`,
      read:false
    }]);
    await sendPushToEmployee(supabase, rec?.emp_name||"", titleKr, `${typeLabel} 신청이 승인되었습니다.`, titleEn, `Your ${typeLabel} request has been approved.`, "/leave", {title:(rec?.type==="특이사항"||rec?.type==="Note")?"Nota aprobada":"Permiso aprobado", body:`Su solicitud de ${dispType(rec?.type,"es")} fue aprobada.`});
    fetchRecords();
  }
  async function rejectLeave(id){
    const rec=records.find(r=>r.id===id);
    await supabase.from("leaves").update({status:"rejected"}).eq("id",id);
    const typeLabel=lang==="kr"?(rec?.type==="연차"?"연차":rec?.type==="병가"?"병가":"특이사항"):(TYPE_EN[rec?.type]||rec?.type);
    const titleKr=rec?.type==="특이사항"?"특이사항 거절":"휴가 거절";
    const titleEn=rec?.type==="특이사항"||rec?.type==="Note"?"Note Rejected":"Leave Rejected";
    await supabase.from("notifications").insert([{
      type:"leave_rejected",
      emp_name:rec?.emp_name||"",
      message:lang==="kr"?`${rec?.emp_name}님의 ${typeLabel} 신청이 거절되었습니다.`:`${rec?.emp_name}'s ${typeLabel} request was rejected.`,
      read:false
    }]);
    await sendPushToEmployee(supabase, rec?.emp_name||"", titleKr, `${typeLabel} 신청이 거절되었습니다.`, titleEn, `Your ${typeLabel} request has been rejected.`, "/leave", {title:(rec?.type==="특이사항"||rec?.type==="Note")?"Nota rechazada":"Permiso rechazado", body:`Su solicitud de ${dispType(rec?.type,"es")} fue rechazada.`});
    fetchRecords();
  }
  async function del(id){
    if(window.confirm(L.confirmDel)){
      const rec=records.find(r=>r.id===id);
      await supabase.from("leaves").delete().eq("id",id);
      const cancelDateMsg=rec?.end_date&&rec.end_date!==rec.date?`${rec.date} ~ ${rec.end_date}`:rec?.date;
      if(!isAdmin&&rec){
        // 직원 취소 시 관리자 알림
        await supabase.from("notifications").insert([{
          type:"leave_cancel",
          emp_name:rec.emp_name,
          message:lang==="kr"?`${rec.emp_name}님이 ${cancelDateMsg} ${rec.type} 신청을 취소했습니다.`:`${rec.emp_name} cancelled ${rec.type} on ${cancelDateMsg}.`,
          read:false
        }]);
        await sendPushToAdmin(supabase, "휴가 신청 취소", `${rec.emp_name}님이 ${cancelDateMsg} ${rec.type === "연차" ? "연차를" : rec.type === "병가" ? "병가를" : rec.type + "을"} 취소했습니다.`, "Leave Cancelled", `${rec.emp_name} cancelled ${TYPE_EN[rec.type]||rec.type} on ${cancelDateMsg}.`, "/leave", {title:"Permiso cancelado", body:`${rec.emp_name} canceló ${dispType(rec.type,"es")} del ${cancelDateMsg}.`});
      } else if(isAdmin&&rec){
        // 관리자 삭제 시 직원에게 알림
        const typeLabel=lang==="kr"?(rec.type==="연차"?"연차":rec.type==="병가"?"병가":"특이사항"):(TYPE_EN[rec.type]||rec.type);
        await sendPushToEmployee(supabase, rec.emp_name, `${typeLabel} 삭제`, `${cancelDateMsg} ${typeLabel} 기록이 관리자에 의해 삭제되었습니다.`, `${typeLabel} Deleted`, `Your ${typeLabel} record for ${cancelDateMsg} has been deleted by the admin.`, "/leave", {title:`${dispType(rec.type,"es")} eliminado`, body:`El administrador eliminó su registro de ${dispType(rec.type,"es")} del ${cancelDateMsg}.`});
      }
      fetchRecords();
    }
  }

  const filtered=records.filter(r=>{
    if(searchLeave!=="all"&&!r.emp_name?.toLowerCase().includes(searchLeave.toLowerCase())) return false;
    if(filterType!=="all"&&r.type!==filterType) return false;
    return true;
  });

  const typeCols=["rgba(59,130,246,.15)","rgba(239,68,68,.15)","rgba(245,158,11,.15)","rgba(107,114,128,.15)"];

  return(
    <>
      <div className="tabs">
        <button className={`tab ${tab==="list"?"active":""}`} onClick={()=>{setTab("list");setEditId(null);setEditingOrig(null);setForm(def);}}>{L.listTab}</button>
        <button className={`tab ${tab==="add"?"active":""}`} onClick={()=>{setTab("add");setEditId(null);setEditingOrig(null);setForm(def);}}>{isAdmin?L.addTab:(L.addTabEmp||"+ 신청")}</button>
      </div>

      {tab==="add"&&(
        <div className="card">
          <div className="ct">{editId?L.editTitle:(isAdmin?L.addTitle:(lang==="kr"?"휴가 신청":lang==="es"?"Solicitud de permiso":"Leave Request"))}</div>
          <div className="fg">
            {isAdmin&&<div className="fl">
              <label>{L.empSel}</label>
              <select value={form.name} onChange={e=>sf("name",e.target.value)}>
                <option value="">{L.placeholder}</option>
                {employees.map(e=><option key={e.name} value={e.name}>{e.name}</option>)}
              </select>
            </div>}
            <div className="fl">
              <label>{L.typeSel}</label>
              <select value={form.type} onChange={e=>sf("type",e.target.value)}>
                {lt.map(tp=><option key={tp} value={tp}>{dispType(tp,lang)}</option>)}
              </select>
            </div>
            {isRangetype(form.type)?(
              <>
                <div className="fl"><label>{L.daysLbl}</label><input type="number" value={calcWorkDays(form.date,form.end_date||form.date)} readOnly style={{opacity:0.7,cursor:"not-allowed"}}/></div>
                <div className="fl"><label>{lang==="kr"?"시작일 *":lang==="es"?"Fecha de inicio *":"Start Date *"}</label><input type="date" value={form.date} onChange={e=>{const nv=e.target.value;sf("date",nv);const ne=nv>form.end_date?nv:form.end_date;if(nv>form.end_date)sf("end_date",nv);checkConflicts(nv,ne);}}/></div>
                <div className="fl"><label>{lang==="kr"?"종료일 *":lang==="es"?"Fecha final *":"End Date *"}</label><input type="date" value={form.end_date||form.date} min={form.date} onChange={e=>{sf("end_date",e.target.value);checkConflicts(form.date,e.target.value);}}/></div>
              </>
            ):(
              <div className="fl"><label>{L.dateLbl}</label><input type="date" value={form.date} onChange={e=>sf("date",e.target.value)}/></div>
            )}
            <div className="fl ff"><label>{L.reasonLbl}</label><input value={form.reason} onChange={e=>sf("reason",e.target.value)} placeholder={L.reasonPh}/></div>
            <div className="fl ff"><label>{L.noteLbl}</label><input value={form.note} onChange={e=>sf("note",e.target.value)} placeholder={L.notePh}/></div>
            {conflictList.length>0&&(
              <div className="fl ff">
                <div style={{background:"rgba(217,119,6,.08)",border:"1px solid rgba(217,119,6,.25)",borderRadius:8,padding:"10px 12px",fontSize:13}}>
                  <b style={{color:"var(--ye)"}}>⚠ {lang==="kr"?"같은 기간 휴가 중":lang==="es"?"Ya tiene permiso en este período":"Already off during this period"}:</b>
                  {conflictList.map((c,i)=>(
                    <div key={i} style={{marginTop:4}}>{c.emp_name} — {dispType(c.type,lang)} ({c.date}{c.end_date&&c.end_date!==c.date?` ~ ${c.end_date}`:""})</div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div style={{marginTop:14,display:"flex",gap:8}}>
            <button className="btn bp" onClick={save}>{L.save}</button>
            <button className="btn bsec" onClick={()=>{setTab("list");setEditId(null);setEditingOrig(null);setForm(def);}}>{L.cancel}</button>
          </div>
        </div>
      )}

      {tab==="list"&&(
        <>
          <div style={{display:"flex",gap:10,marginBottom:12,flexWrap:"wrap"}}>
            {isAdmin&&<>
              <button onClick={()=>{setShowSearchLeave(s=>!s);if(showSearchLeave)setSearchLeave("all");}} style={{background:"var(--sf2)",border:"1px solid var(--bd)",borderRadius:8,padding:"6px 10px",cursor:"pointer",fontSize:16,color:"var(--tx2)",lineHeight:1}}><Search size={15}/></button>
              {showSearchLeave&&<input autoFocus value={searchLeave==="all"?"":searchLeave} onChange={e=>setSearchLeave(e.target.value||"all")}
                placeholder={L.allEmp}
                style={{padding:"6px 14px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",fontSize:13,width:140}}
              />}
            </>}
            <select value={filterType} onChange={e=>setFilterType(e.target.value)} style={{background:"var(--sf2)",border:"1px solid var(--bd)",borderRadius:8,padding:"6px 10px",color:"var(--tx)",fontSize:13}}>
              <option value="all">{L.allType}</option>
              {lt.map(tp=><option key={tp} value={tp}>{dispType(tp,lang)}</option>)}
            </select>
          </div>
          {filtered.length===0?<div className="card"><div className="empty">{L.noData||"기록 없음"}</div></div>:
          filtered.map((rec,i)=>(
            <div className="card" key={rec.id} style={{borderLeft:`4px solid ${typeCols[lt.indexOf(rec.type)]||"var(--bd)"}`.replace("rgba(","rgba(").replace(".15)","1)")}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:8}}>
                <div>
                  <div style={{fontWeight:700,fontSize:14,marginBottom:4}}>{rec.emp_name}
                    <span style={{marginLeft:8,padding:"2px 10px",borderRadius:20,fontSize:11,fontWeight:700,
                      background:typeCols[lt.indexOf(rec.type)]||"var(--sf2)"}}>{dispType(rec.type,lang)}</span>
                    <span style={{marginLeft:6,fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:20,
                      background:rec.status==="approved"?"rgba(5,150,105,.12)":rec.status==="rejected"?"rgba(239,68,68,.12)":"rgba(217,119,6,.12)",
                      color:rec.status==="approved"?"var(--gr)":rec.status==="rejected"?"var(--re)":"var(--ye)"
                    }}>{rec.status==="approved"?(lang==="kr"?"승인":lang==="es"?"Aprobado":"Approved"):rec.status==="rejected"?(lang==="kr"?"거절":lang==="es"?"Rechazado":"Rejected"):(lang==="kr"?"대기중":lang==="es"?"Pendiente":"Pending")}</span>
                  </div>
                  <div style={{fontSize:12,color:"var(--tx3)"}}>
                    <Calendar size={11}/> {rec.date}{rec.end_date&&rec.end_date!==rec.date?` ~ ${rec.end_date}`:""} {rec.days&&<span>· {rec.days}{lang==="kr"?"일":lang==="es"?"d":"d"}</span>}
                    {rec.reason&&<span> · {rec.reason}</span>}
                  </div>
                  {rec.note&&<div style={{fontSize:12,color:"var(--tx3)",marginTop:2}}><FileText size={12} style={{marginRight:4}}/>{rec.note}</div>}
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {isAdmin&&rec.status==="pending"&&<button className="btn bs" style={{background:"rgba(5,150,105,.1)",color:"var(--gr)",border:"1px solid rgba(5,150,105,.3)"}} onClick={()=>approveLeave(rec.id)}><CheckCircle size={14} style={{marginRight:4}}/>{lang==="kr"?"승인":lang==="es"?"Aprobar":"Approve"}</button>}
                  {isAdmin&&rec.status==="pending"&&<button className="btn bs" style={{background:"rgba(239,68,68,.1)",color:"var(--re)",border:"1px solid rgba(239,68,68,.3)"}} onClick={()=>rejectLeave(rec.id)}><XCircle size={14} style={{marginRight:4}}/>{lang==="kr"?"거절":lang==="es"?"Rechazar":"Reject"}</button>}
                  {isAdmin&&<button className="btn bs" style={{background:"var(--sf2)",color:"var(--tx2)",border:"1px solid var(--bd)"}} onClick={()=>startEdit(rec)}>{L.edit}</button>}
                  {isAdmin&&<button className="btn bs bd2" onClick={()=>del(rec.id)}>{L.del}</button>}
                  {!isAdmin&&rec.status==="pending"&&<button className="btn bs bd2" onClick={()=>del(rec.id)}>{lang==="kr"?"취소":lang==="es"?"Cancelar":"Cancel"}</button>}
                  {!isAdmin&&rec.status==="approved"&&(rec.end_date||rec.date)>=today&&<>
                    <button className="btn bs" style={{background:"var(--sf2)",color:"var(--tx2)",border:"1px solid var(--bd)"}} onClick={()=>startEdit(rec)}>{lang==="kr"?"수정":lang==="es"?"Editar":"Edit"}</button>
                    <button className="btn bs bd2" onClick={()=>del(rec.id)}>{lang==="kr"?"취소":lang==="es"?"Cancelar":"Cancel"}</button>
                  </>}
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </>
  );
}
